import React, { JSX, useCallback, useMemo } from "react";
import { withImageSrc } from "../../data/textures";
import { getPriceWithCurrency } from "../../utils/formatPrice";
import { ActiveHolder } from "../ActiveHolder/ActiveHolder";
import { Box } from "@mui/material";
import {
  HybridCarousel,
  HybridCarouselProps,
} from "../HybridCarousel/HybridCarousel";
import { MainCategoriesEnum, UID } from "@shared/core";
import {
  useActiveBucket,
  useActiveCategory,
  useActiveProduct,
  useMainCategoryStaticData,
  useSelection,
} from "../../store/selectors";
import { useConfigStore } from "../../store";
import { calcFlexOrdersSigned } from "../../utils/calcFlexOrders";
import { usePriceDelta } from "../../hooks/usePriceDelta";
import { useDeviceStyles } from "src/hooks/useDeviceStyles";

export type GoodsListProps = {
  activeIndex: number;
  setIndexForGroup: (groupUid: string, nextIndex: number) => void;
  carouselProps?: HybridCarouselProps;
};

export const GoodsList: React.FC<GoodsListProps> = (props): JSX.Element => {
  const { activeIndex, setIndexForGroup, carouselProps } = props;

  const activeProduct = useActiveProduct();
  const activeCategory = useActiveCategory();
  const activeBucket = useActiveBucket(activeCategory);
  const selection = useSelection(activeCategory);
  const mainCategoryStaticData = useMainCategoryStaticData();
  const { isMobileDevice } = useDeviceStyles();

  const selectSingle = useConfigStore((s) => s.selectSingle);
  const toggleMulti = useConfigStore((s) => s.toggleMulti);

  const { getPriceDelta } = usePriceDelta();

  const activeIndexFromSelection = useMemo(() => {
    const last =
      selection?.kind === "single"
        ? selection.uid
        : selection?.uids[selection?.uids.length - 1];
    return activeBucket.items.findIndex((item) => item.uid === last);
  }, [activeBucket.items, selection]);

  const ordersItems = useMemo(() => {
    return calcFlexOrdersSigned(
      activeBucket.items.length,
      activeIndexFromSelection,
    );
  }, [activeBucket.items.length, activeIndexFromSelection]);

  const handleItemChange = useCallback(
    (idx: number) => {
      if (!activeBucket) return;

      const groupUid = activeBucket.relation.uid;
      const item = activeBucket.items[idx];
      if (!item) return;

      // ✅ prevent redundant updates
      if (idx === activeIndex) return;

      setIndexForGroup(groupUid, idx);

      if (selection?.kind === "single") {
        selectSingle(activeCategory, item.uid as UID);
      }
    },
    [
      activeBucket,
      activeCategory,
      selection?.kind,
      selectSingle,
      setIndexForGroup,
      activeIndex, // 👈 IMPORTANT
    ],
  );

  const itemWidth = useMemo(() => {
    switch (activeCategory) {
      case MainCategoriesEnum.PROPULSION:
        return isMobileDevice ? 132 : 230;
      case MainCategoriesEnum.EXTRAS:
        return isMobileDevice ? 108 : 180;

      default:
        return isMobileDevice ? 66 : 110;
    }
  }, [activeCategory]);

  return (
    <HybridCarousel
      itemWidth={itemWidth}
      onSlideChange={handleItemChange}
      activeIndices={[activeIndex]}
      key={`${activeProduct}-${activeCategory}-${activeBucket.relation.uid}`}
      anchor={`${activeProduct}-${activeCategory}-${activeBucket.relation.uid}`}
      orders={ordersItems}
      children={activeBucket?.items.map((el: any) => {
        const { price, uid, emitId, ...rest } = el;
        const src: string = withImageSrc([el])?.[0].src;
        const isActive =
          selection?.kind === "single"
            ? selection?.uid === el.uid
            : selection?.uids.includes(el.uid);
        const type = mainCategoryStaticData.type;

        let priceFinal = undefined;
        /* workaround for '' group */
        if (
          activeBucket?.relation?.uid === "metallic" ||
          activeBucket?.relation?.uid === "teak_natural"
        ) {
          priceFinal = getPriceWithCurrency(price, "+");
        } else if (activeBucket.items.some((sub) => sub.price > 0)) {
          priceFinal = getPriceDelta(price);
        }
        return (
          <ActiveHolder
            key={el.uid}
            anchor={el.uid}
            price={priceFinal}
            active={isActive}
            type={type}
            onClick={() => {
              if (selection?.kind === "multi") {
                toggleMulti(activeCategory, el.uid);
              }
            }}
            // sx={{
            //   width: {
            //     lg: '300px',
            //     xl: '350px',
            //     xxxl: '540px',
            //   }
            // }}
            {...rest}
          >
            {src && (
              <Box
                component={"img"}
                src={src}
                alt={`${activeCategory}-${el.name}`}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  height: "auto",
                  zIndex: 2,
                }}
              />
            )}
          </ActiveHolder>
        );
      })}
      {...carouselProps}
    />
  );
};
